import type { DialDocumentRow } from "@keydials/api-client";

/**
 * The pure half of a Group Key Dial's rolled-up Files tab (KEY-643): which dial a row's per-file calls go to,
 * what the row says about where it came from, what a ZIP request carries, and what an empty table says. Kept
 * out of `InstanceFilesPanel` so each rule is testable on its own — the two that matter most here fail
 * SILENTLY when they are wrong: a subdial's file downloaded under the group's id is a 404, and a ZIP of ticked
 * subdial files sent without `includeChildren` is an archive the server quietly left them out of.
 */

/**
 * The dial a row's download (and any other per-file call) must go to: the row's own, which on a group's rolled-up
 * list is often a subdial. Falls back to the dial being browsed when the server predates the field.
 */
export function downloadTargetId(row: Pick<DialDocumentRow, "instanceId">, hostInstanceId: string): string {
  return row.instanceId ?? hostInstanceId;
}

/**
 * The "From: …" line under a row's location on a group's list: the subdial's name, or null for the group's own
 * files (and on a single dial, where every row is the dial the reader is already looking at).
 */
export function rowDialLabel(
  row: Pick<DialDocumentRow, "instanceId" | "dialName">,
  hostInstanceId: string,
  isGroup: boolean,
): string | null {
  if (!isGroup || !row.instanceId || row.instanceId === hostInstanceId) {
    return null;
  }
  return row.dialName || null;
}

/** What the Files tab's table is currently narrowed to — everything a "download what I'm looking at" needs. */
export interface FileViewNarrowing {
  isGroup: boolean;
  sectionKey: string | null;
  segmentKey: string | null;
  dialId: string | null;
  tags: string[];
  query: string;
}

/** The last two arguments of `api.downloadDocumentsZip`. */
export interface ZipCall {
  documentIds: string[] | undefined;
  scope:
    | {
        sectionKey?: string | null;
        segmentKey?: string | null;
        tags?: string[] | null;
        q?: string | null;
        includeChildren?: boolean;
        dialId?: string | null;
      }
    | undefined;
}

/**
 * The ZIP request for a set of ticked ids (`documentIds`), or — `undefined` — for everything the table is showing.
 *
 * By ID: exactly the ticked rows, wherever they are, so NO narrowing goes with them — the server would intersect
 * the two and drop every ticked row outside the current view. On a group, `includeChildren` still goes: it is not
 * a narrowing but WHICH SET the ids are looked up in, and without it every ticked subdial file is silently dropped.
 *
 * By SCOPE: "everything here", sent as the intent rather than a list — scope and filter both, because that is
 * what the table beside the button is showing. On a group the scope is the dial picker, never a section.
 */
export function zipRequest(documentIds: string[] | undefined, view: FileViewNarrowing): ZipCall {
  if (documentIds) {
    return { documentIds, scope: view.isGroup ? { includeChildren: true } : undefined };
  }
  if (view.isGroup) {
    return {
      documentIds: undefined,
      scope: { includeChildren: true, dialId: view.dialId, tags: view.tags, q: view.query },
    };
  }
  const narrowed = view.sectionKey !== null || view.segmentKey !== null || view.tags.length > 0 || view.query !== "";
  return {
    documentIds: undefined,
    scope: narrowed ? { sectionKey: view.sectionKey, segmentKey: view.segmentKey, tags: view.tags, q: view.query } : undefined,
  };
}

/**
 * What an empty table says. "Nothing here" and "nothing matches what you typed" are different facts, and telling
 * somebody the first when the second is true sends them looking for a file that is right there. On a group the
 * narrowest thing named is the picked dial; otherwise the section/action point, then the dial as a whole.
 */
export function emptyMessage(opts: {
  filtered: boolean;
  isGroup: boolean;
  dialName?: string | null;
  sectionLabel?: string | null;
  segmentName?: string | null;
}): string {
  if (opts.filtered) {
    return "No files match that search.";
  }
  if (opts.isGroup) {
    return opts.dialName
      ? `No files have been uploaded to “${opts.dialName}” yet.`
      : "No files have been uploaded to this dial or its subdials yet.";
  }
  if (opts.segmentName) {
    return `No files have been uploaded to “${opts.segmentName}” yet.`;
  }
  if (opts.sectionLabel) {
    return `No files have been uploaded to “${opts.sectionLabel}” yet.`;
  }
  return "No files have been uploaded to this dial yet.";
}
